import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { DynamicFormComponent } from 'src/app/components/dynamic-form/dynamic-form.component';
import { IconModule } from 'src/app/icon/icon.module';
import { AuthService } from 'src/app/services/auth.service';
import { CountryService } from 'src/app/services/country.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import { PATTERN } from 'src/app/shared/constants';
import { FormControlType } from 'src/app/shared/enums';
import { IFormConfig } from 'src/app/shared/interfaces/IForm';
import { MaterialModule } from "src/app/material.module";
import { Router } from '@angular/router';
import { AdminService } from 'src/app/services/admin.service';

@Component({
  selector: 'app-personal-information',
  imports: [IconModule, DynamicFormComponent, CommonModule, MaterialModule],
  templateUrl: './personal-information.component.html',
  styleUrl: './personal-information.component.scss'
})
export class PersonalInformationComponent {
  userInfoForm: FormGroup;
  formConfigs: IFormConfig;
  spinLoader = false;

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private translateService: TranslateService,
    private authService: AuthService,
    private userService: UserService,
    private adminService: AdminService,
    private countryService: CountryService) {
      this.initForm();
      this.patchUserData();
  }

  get userInfo() {
    return this.isAdmin ? this.adminService.adminInfo : this.authService.userInfo;
  }

  get isAdmin () {
    return this.router.url.startsWith('/admin');
  }

  initForm() {
    this.userInfoForm = this.formBuilder.group({
      id: [null],
      email: [{ value: null }, [Validators.required, Validators.pattern(PATTERN.EMAIL)]],
      name: [{ value: null }, [Validators.required, Validators.maxLength(50)]],
      phone: [{ value: null }, [Validators.pattern(PATTERN.PHONE)]],
      address: [{ value: null }, [Validators.maxLength(50)]],
      zipCode: [{ value: null }, [Validators.maxLength(20), Validators.pattern(PATTERN.ZIP_GENERIC)]],
      city: [{ value: null}, [Validators.maxLength(50)]],
      countryId: [{ value: null }]
    });

    this.formConfigs = {
      id: 'userInfoForm',
      styles: {
        bodyClass: 'p-0',
        footerClass: 'flex justify-end border-t border-[#e0e2ec] p-4!'
      },
      actions: [
        {
          text: 'save',
          type: 'submit',
          id:"button-submit-user-info",
          class: 'h-[36px]! text-[12px]! cursor-pointer!'
        }
      ],
      items: [
        {
          key: 'name',
          label: 'full name',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT,
          validator: [
            { errorType: 'required', errorMessage: 'full name required' },
            { 
              errorType: 'maxlength', 
              errorMessage: 'full name length must not exceed',
              params:{ length: 50 }
            }
          ]
        },
        {
          key: 'email',
          label: 'email',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT,
          validator: [{ errorType: 'pattern', errorMessage: 'email is invalid' }]
        },
        {
          key: 'phone',
          label: 'phone',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT,
          validator: [{ errorType: 'pattern', errorMessage: 'phone is invalid' }]
        },
        {
          key: 'zipCode',
          label: 'zip code',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT,
          validator: [{ errorType: 'pattern', errorMessage: 'zip code is invalid' }]
        },
        {
          key: 'city',
          label: 'city',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT,
          validator: [{ errorType: 'maxlength', errorMessage: 'city length must not exceed', params:{ length: 50 }}]
        },
        {
          key: 'countryId',
          label: 'country',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.SELECT,
          fetchDataAPI: () => {
            return this.countryService.getCountries({pageSize: 1000})
          },
          selectionConfig: {
            title: 'name',
            key: 'id'
          }
        },
        {
          key: 'address',
          label: 'address',
          layoutConfig: [{ column: 12 }],
          controlType: FormControlType.INPUT,
          validator: [{ errorType: 'maxlength', errorMessage: `address length must not exceed`, params:{ length: 50 }}]
        }
      ]
    }
  }

  private patchUserData(): void {
    if (this.userInfo) this.userInfoForm.patchValue(this.userInfo);
  }


  onSubmit = () => {
    if (!this.userInfoForm.valid) return this.userInfoForm.markAllAsTouched();
    this.spinLoader = true;
    const payload = { ...this.userInfo, ...this.userInfoForm.value };

    const service = this.isAdmin ? this.adminService : this.userService;
    service.updateUserInfo(payload).pipe(finalize(() => (this.spinLoader = false))).subscribe({
      next: (res: any) => {
        if (res) {
          this.isAdmin ? this.adminService.adminInfo = payload : this.authService.userInfo = payload;
          this.toastService.success(this.translateService.instant('update successfully'));
        }
      },
      error: () => this.toastService.error(this.translateService.instant('update failed'))
    });
  }
}
